import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import type { ApiFailureError } from '../../../../lib/http/errors';
import { useApiClient } from '../../../session/client';
import { type Place } from '../../accounts/api/schemas';
import { type CandidatureScope, type Election, type Registration, type TseOffice } from './schemas';
import { GET_ROUTES, routePath } from '../../../../lib/http/routes';

const SEARCH_LIMIT = 8;

export function useElections(tenantId: string): UseQueryResult<Election[], ApiFailureError> {
  const client = useApiClient();
  return useQuery<Election[], ApiFailureError>({
    queryKey: ['campaign', 'tse', 'elections', tenantId],
    queryFn: async () => {
      const { items } = await client.getParsed(
        routePath(GET_ROUTES.tenantElections, { tenantId }),
        GET_ROUTES.tenantElections.schema,
      );
      return items;
    },
    enabled: tenantId !== '',
  });
}

/** TSE's office table, the Cargo list in both picker modes. */
export function useTseOffices(tenantId: string): UseQueryResult<TseOffice[], ApiFailureError> {
  const client = useApiClient();
  return useQuery<TseOffice[], ApiFailureError>({
    queryKey: ['campaign', 'tse', 'offices', tenantId],
    queryFn: async () => {
      const { items } = await client.getParsed(
        routePath(GET_ROUTES.tseOffices, { tenantId }),
        GET_ROUTES.tseOffices.schema,
      );
      return items;
    },
    enabled: tenantId !== '',
    staleTime: Infinity,
  });
}

/** The units the chosen election's roster holds for the chosen office: TSE mode's Estado list. */
export function useTseUnits(
  tenantId: string,
  electionId: string,
  office: string | null,
): UseQueryResult<string[], ApiFailureError> {
  const client = useApiClient();
  return useQuery<string[], ApiFailureError>({
    queryKey: ['campaign', 'tse', 'units', tenantId, electionId, office],
    queryFn: async () => {
      const params = new URLSearchParams({ election_id: electionId, office: office ?? '' });
      const { items } = await client.getParsed(
        routePath(GET_ROUTES.tseUnits, { tenantId }, params),
        GET_ROUTES.tseUnits.schema,
      );
      return items;
    },
    enabled: tenantId !== '' && electionId !== '' && office !== null,
  });
}

/** The tenant's state places (`GET /tenants/{t}/places?level=state`), the Estado list: 27 rows,
 *  inside the route's own 50-row page. */
export function useStates(tenantId: string): UseQueryResult<Place[], ApiFailureError> {
  const client = useApiClient();
  return useQuery<Place[], ApiFailureError>({
    queryKey: ['tenant', tenantId, 'places', 'state', ''],
    queryFn: async () => {
      const { items } = await client.getParsed(
        routePath(GET_ROUTES.places, { tenantId }, { level: 'state', q: '' }),
        GET_ROUTES.places.schema,
      );
      return items;
    },
    enabled: tenantId !== '',
  });
}

export interface TseCandidateSearch {
  electionId: string;
  office: string | null;
  electoralUnit: string | null;
  ballotNumber: string | null;
  q: string;
}

/** Every dimension that changes what a valid answer looks like -- narrowing any one of them
 *  without changing the key would serve the wider search's cached result back unchanged. */
export function useTseCandidates(
  tenantId: string,
  search: TseCandidateSearch,
): UseQueryResult<Registration[], ApiFailureError> {
  const client = useApiClient();
  return useQuery<Registration[], ApiFailureError>({
    queryKey: [
      'campaign',
      'tse',
      'candidates',
      tenantId,
      search.electionId,
      search.office,
      search.electoralUnit,
      search.ballotNumber,
      search.q,
    ],
    queryFn: async () => {
      const params = new URLSearchParams({ election_id: search.electionId });
      if (search.office !== null) params.set('office', search.office);
      if (search.electoralUnit !== null) params.set('electoral_unit', search.electoralUnit);
      if (search.ballotNumber !== null) params.set('ballot_number', search.ballotNumber);
      if (search.q.trim() !== '') params.set('q', search.q.trim());
      params.set('limit', String(SEARCH_LIMIT));
      const { items } = await client.getParsed(
        routePath(GET_ROUTES.tseCandidates, { tenantId }, params),
        GET_ROUTES.tseCandidates.schema,
      );
      return items;
    },
    enabled: tenantId !== '' && search.electionId !== '',
  });
}

/** `electionId`/`tseId` are `null` before a candidature is picked (or for a manually-typed one,
 *  which carries no TSE identity to resolve): `enabled` stays false rather than the caller
 *  skipping the hook, since a hook cannot be called conditionally. */
export function useCandidatureScope(
  tenantId: string,
  electionId: string | null,
  tseId: string | null,
): UseQueryResult<CandidatureScope, ApiFailureError> {
  const client = useApiClient();
  return useQuery<CandidatureScope, ApiFailureError>({
    queryKey: ['campaign', 'tse', 'scope', tenantId, electionId, tseId],
    queryFn: () => {
      const params = new URLSearchParams({ election_id: electionId ?? '', tse_id: tseId ?? '' });
      return client.getParsed(
        routePath(GET_ROUTES.tseCandidateScope, { tenantId }, params),
        GET_ROUTES.tseCandidateScope.schema,
      );
    },
    enabled: tenantId !== '' && electionId !== null && tseId !== null,
  });
}
